import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { pickTagColor, type TagOption } from '@troia-v3/types';

import { api, type ApiResponse } from '@/lib/api';

/** Chave única do catálogo. Quem cria/edita tag invalida ESTA chave. */
export const TAG_CATALOG_QUERY_KEY = ['tag-catalog'] as const;

/**
 * Catálogo de tags do tenant, para qualquer tela que precise desenhar um chip.
 *
 * Usa `GET /api/tags/dropdown` — rota cross-module sem `permissionMiddleware`
 * (só app + auth), pela mesma razão de `useUnitsDropdown`: marcar um contato
 * é trabalho de quem atende, e exigir a permissão do CATÁLOGO para ver um chip
 * colorido obrigaria a conceder `tags:read` a todo mundo.
 *
 * `enabled` (default `true`) — condicionar a `isOpen` em modal/drawer evita
 * disparar a query com o componente fechado (CLAUDE.md SEMPRE #10).
 */
export const useTagCatalog = (enabled: boolean = true): UseQueryResult<TagOption[], Error> =>
  useQuery({
    queryKey: TAG_CATALOG_QUERY_KEY,
    queryFn: async () => {
      const response = await api.get<ApiResponse<TagOption[]>>('/tags/dropdown');
      return response.data.data ?? [];
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });

/** Estilo de um chip de tag: fundo esmaecido + texto e borda na cor sólida. */
export interface TagChipStyle {
  backgroundColor: string;
  color: string;
  borderColor: string;
}

const chipStyle = (hex: string): TagChipStyle => ({
  // `1A` = 10% de opacidade em hex. Deriva o fundo da MESMA cor em vez de
  // guardar duas — e funciona nos dois temas, porque é translúcido sobre o
  // fundo do cartão em vez de ser um cinza-claro fixo.
  backgroundColor: `${hex}1A`,
  color: hex,
  borderColor: `${hex}33`,
});

/**
 * Resolve nome de tag → estilo do chip.
 *
 * Devolve uma FUNÇÃO (e não um mapa) porque o caso de borda importa: uma tag
 * que está no contato mas não no catálogo — uma órfã — precisa continuar
 * aparecendo. Ela cai em `pickTagColor`, o mesmo hash determinístico que o
 * catálogo usa para atribuir cor, então ela sai com exatamente a cor que teria
 * se estivesse cadastrada. Ninguém vê um chip cinza "quebrado".
 *
 * Órfã é situação transitória por desenho (toda escrita passa pelo catálogo),
 * mas existe de verdade em dois momentos: entre o deploy e a migração, e
 * depois de alguém excluir uma tag sem purgar as marcações.
 */
export const useTagStyle = (enabled: boolean = true): ((tagName: string) => TagChipStyle) => {
  const { data } = useTagCatalog(enabled);

  return useMemo(() => {
    const porNome = new Map<string, string>();
    for (const tag of data ?? []) porNome.set(tag.name, tag.color);

    return (tagName: string): TagChipStyle => chipStyle(porNome.get(tagName) ?? pickTagColor(tagName));
  }, [data]);
};

/**
 * Versão sem hook, para quem já tem o catálogo em mãos (listas que renderizam
 * centenas de chips e não podem chamar o hook por item).
 */
export const tagStyleFrom = (catalog: TagOption[] | undefined, tagName: string): TagChipStyle => {
  const encontrada = catalog?.find((tag) => tag.name === tagName);
  return chipStyle(encontrada?.color ?? pickTagColor(tagName));
};
